import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import { memo, useContext } from 'react';
import { useBuildNotificationFromJson } from '@/modules/common/hooks/useBuildNotificationFromJson.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerReadNotificationModal } from '@/modules/issuer-dashboard/components/modals/IssuerReadNotificationModal.tsx';
import { useIssuerMakeIsReadNotificationsMutation } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { DisclosureListItem, ModalProviderContext } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EventLogResponse } from 'api';

interface IssuerNotificationItemProps {
  notification: EventLogResponse;
}

const IssuerNotificationItem = ({ notification }: IssuerNotificationItemProps) => {
  const getRegisterTypeString = useRegisterTypeStrings();
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync } = useIssuerMakeIsReadNotificationsMutation();
  const text = useBuildNotificationFromJson(
    notification.type,
    notification.data,
    true,
    rolesManipulator.current_user_role === Role.SR_ISSUER ||
      rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN,
  );
  //
  const isActive = (notification?.data as any)?.['is_active'] ?? false;
  const isHistorical = (notification?.data as any)?.['is_historical'] ?? false;
  const isFuture = (notification?.data as any)?.['is_future'] ?? false;

  const handleOpenModal = async () => {
    if (!notification.is_read) {
      mutateAsync({ ids: [notification.id] });
    }
    openModal(IssuerReadNotificationModal, {
      type: notification.type,
      data: notification.data,
      created_at: dateToPrint(notification?.created_at) || '-',
    });
  };

  return (
    <DisclosureListItem
      hoverTitle="Click to show popup with full information"
      onClick={handleOpenModal}
      leftContent={
        <div
          className={`mt-[7px] w-2 h-2 rounded-full ${
            notification.is_read ? 'bg-gray-200' : 'bg-red-600'
          }`}
        />
      }
      leftSubduedContent={
        <div
          className={classNames(
            'text-xs',
            'max-w-[50dvw] sm:max-w-[150px] md:max-w-[37dvw] lg:max-w-[52dvw] xl:max-w-[62dvw] 2xl:max-w-[68dvw]',
          )}
        >
          {text}
        </div>
      }
      leftMainString={
        <div>
          {isActive ? 'Active' : isHistorical ? 'Historical' : isFuture ? 'Future' : ''}{' '}
          {fixNameEnum(notification.type)
            .replace('Correspondence Reply Created', 'Email Reply Received')
            .replace('Correspondence Created', 'Email Received')
            .replace('Nav', getRegisterTypeString('Nav'))
            .replace('Dividend', getRegisterTypeString('Dividend', { capitalize: true }))
            .replace('Finalized', 'Finalised')
            .replace(/Unsuccessfull/gi, 'Unsuccessful')}
        </div>
      }
      rightTopSubduedString={dateToPrint(notification?.created_at) || '-'}
      rightMainString={<div className="h-5"></div>}
      classes={{ rightMainString: 'sm:w-[110px]' }}
    />
  );
};

export default memo(IssuerNotificationItem);
